import React, { useEffect, useMemo, useState, useCallback } from "react";
import { BACKEND_URL } from "../config";

const DEFAULT_METRIC_RULE = {
  domain: "food",
  field: "calories",
  operator: "lt",
  value: "",
};

const KNOWN_UNITS = [
  "lbs",
  "percent",
  "miles",
  "km",
  "minutes",
  "days",
  "workouts",
  "calories",
  "steps",
  "reps",
  "sessions",
];

export default function GoalModal({
  open,
  onClose,
  token,
  currentUserId,
  canManageOthers = false,
  people = [],
  isAdmin = false,
  defaultAssignUserId = "me",
  onCreated,
  showToast,
}) {
  const [assignUserId, setAssignUserId] = useState(defaultAssignUserId);

  const [goalInput, setGoalInput] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [targetValue, setTargetValue] = useState("");
  const [targetUnit, setTargetUnit] = useState("");
  const [customUnit, setCustomUnit] = useState("");
  const [dueDate, setDueDate] = useState("");

  const [metricEnabled, setMetricEnabled] = useState(false);
  const [metricOptions, setMetricOptions] = useState({ domains: [], fields: {}, connected: {} });
  const [metricRule, setMetricRule] = useState(DEFAULT_METRIC_RULE);

  const [milestones, setMilestones] = useState([]);
  const [msTitle, setMsTitle] = useState("");
  const [msDesc, setMsDesc] = useState("");
  const [msTargetValue, setMsTargetValue] = useState("");
  const [msTargetUnit, setMsTargetUnit] = useState("");
  const [msDueDate, setMsDueDate] = useState("");
  const [msCount, setMsCount] = useState(5);
  const [msLoading, setMsLoading] = useState(false);

  const [aiMakeMeasurable, setAiMakeMeasurable] = useState(true);
  const [aiSuggestDate, setAiSuggestDate] = useState(true);
  const [aiUseHistory, setAiUseHistory] = useState(true);
  const [aiHistoryDays, setAiHistoryDays] = useState(90);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const normalizedPeople = useMemo(() => {
    return (people || [])
      .filter((p) => p && p.user_id)
      .map((p) => ({
        user_id: p.user_id,
        name: p.name || p.email || p.user_id,
        email: p.email || "",
        role: p.role || "",
      }));
  }, [people]);

  const currentUserRole = useMemo(() => {
    const me = normalizedPeople.find((p) => p.user_id === currentUserId);
    return me?.role || "";
  }, [normalizedPeople, currentUserId]);

  const currentUserIsAdmin = isAdmin || currentUserRole === "admin";

  const resolvedAssignUserId = useMemo(() => {
    if (!canManageOthers) return currentUserId;
    if (!assignUserId || assignUserId === "me") return currentUserId;
    return assignUserId;
  }, [assignUserId, canManageOthers, currentUserId]);

  const resolvedTargetUnit = useMemo(() => {
    return targetUnit === "custom" ? customUnit.trim() : targetUnit.trim();
  }, [targetUnit, customUnit]);

  const metricFields = useMemo(() => {
    const d = (metricRule?.domain || "food").toLowerCase();
    const fields = metricOptions.fields?.[d];
    return Array.isArray(fields) && fields.length ? fields : ["calories"];
  }, [metricRule?.domain, metricOptions]);

  const metricDomains = useMemo(() => {
    const domains = Array.isArray(metricOptions?.domains) ? metricOptions.domains : [];
    return domains.length ? domains : ["food", "exercise", "wearables", "weight"];
  }, [metricOptions]);

  const loadGoalMetricOptions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/habits/metric-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setMetricOptions(data || { domains: [], fields: {}, connected: {} });
    } catch {
      // ignore
    }
  }, [token]);

  const resetMilestoneInputs = useCallback(() => {
    setMsTitle("");
    setMsDesc("");
    setMsTargetValue("");
    setMsTargetUnit("");
    setMsDueDate("");
  }, []);

  const resetForm = useCallback(() => {
    setAssignUserId(defaultAssignUserId);
    setGoalInput("");
    setTitle("");
    setDescription("");
    setTargetValue("");
    setTargetUnit("");
    setCustomUnit("");
    setDueDate("");

    setMetricEnabled(false);
    setMetricRule(DEFAULT_METRIC_RULE);

    setMilestones([]);
    resetMilestoneInputs();
    setMsCount(5);
    setMsLoading(false);

    setAiMakeMeasurable(true);
    setAiSuggestDate(true);
    setAiUseHistory(true);
    setAiHistoryDays(90);
    setAiLoading(false);
    setSaving(false);
  }, [defaultAssignUserId, resetMilestoneInputs]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    resetForm();
    loadGoalMetricOptions();
  }, [open, resetForm, loadGoalMetricOptions]);

  useEffect(() => {
    if (!open) return;
    if (!metricEnabled) return;
    const f = metricRule?.field;
    if (f && metricFields.includes(f)) return;
    setMetricRule((prev) => ({ ...prev, field: metricFields[0] || "calories" }));
  }, [open, metricEnabled, metricRule?.field, metricFields]);


  const close = useCallback(() => {
  if (saving || aiLoading || msLoading) return;
  onClose?.();
}, [saving, aiLoading, msLoading, onClose]);

useEffect(() => {
  if (!open) return;
  const onKeyDown = (e) => {
    if (e.key === "Escape") close();
  };
  window.addEventListener("keydown", onKeyDown);
  return () => window.removeEventListener("keydown", onKeyDown);
}, [open, close]);

  const addMilestoneLocal = () => {
    const nextTitle = msTitle.trim();
    if (!nextTitle) return;

    setMilestones((prev) => [
      ...prev,
      {
        title: nextTitle,
        description: msDesc.trim() || null,
        target_value: msTargetValue !== "" ? Number(msTargetValue) : null,
        target_unit: msTargetUnit.trim() || null,
        due_date: msDueDate || null,
      },
    ]);

    resetMilestoneInputs();
  };

  const removeMilestoneLocal = (idx) => {
    setMilestones((prev) => prev.filter((_, i) => i !== idx));
  };

  const runAiSuggest = async () => {
    if (!token) return;
    const idea = goalInput.trim() || title.trim() || description.trim();
    if (!idea) {
      showToast?.('Type a goal first (e.g. "Run a marathon", "Lower body fat").', "error");
      return;
    }

    setAiLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/ai/suggest`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: resolvedAssignUserId,
          goal_input: idea,
          make_measurable: aiMakeMeasurable,
          suggest_target_date: aiSuggestDate,
          include_history: aiUseHistory,
          history_days: aiHistoryDays,
        }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "AI suggest failed");
      }

      const out = await res.json();

      if (out.title) setTitle(String(out.title));
      if (out.description != null) setDescription(String(out.description));
      if (out.target_value != null && out.target_value !== "") setTargetValue(String(out.target_value));

      if (out.target_unit != null && out.target_unit !== "") {
        const suggestedUnit = String(out.target_unit);
        if (KNOWN_UNITS.includes(suggestedUnit)) {
          setTargetUnit(suggestedUnit);
          setCustomUnit("");
        } else {
          setTargetUnit("custom");
          setCustomUnit(suggestedUnit);
        }
      }

      if (out.due_date) setDueDate(String(out.due_date));
    } catch (e) {
      showToast?.("AI goal assist error: " + (e?.message || String(e)), "error");
    } finally {
      setAiLoading(false);
    }
  };

  const generateMilestonesAI = async () => {
    if (!token) return;

    const finalTitle = (title || goalInput || "").trim();
    if (!finalTitle) {
      showToast?.("Enter a goal title first.", "error");
      return;
    }

    setMsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/ai/milestones`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: resolvedAssignUserId,
          goal_title: finalTitle,
          goal_description: description.trim() || null,
          target_value: targetValue !== "" ? Number(targetValue) : null,
          target_unit: resolvedTargetUnit || null,
          due_date: dueDate || null,
          milestone_count: msCount || 5,
        }),
      });

      if (!res.ok) throw new Error(await res.text());
      const out = await res.json();
      const nextMilestones = Array.isArray(out?.milestones) ? out.milestones : [];

      if (!nextMilestones.length) {
        showToast?.("No milestones were generated.", "info");
        return;
      }

      setMilestones((prev) => [...prev, ...nextMilestones]);
    } catch (e) {
      showToast?.("Error generating milestones: " + (e?.message || String(e)), "error");
    } finally {
      setMsLoading(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!token) return;

    const finalTitle = (title || goalInput || "").trim();
    if (!finalTitle) {
      showToast?.("Please enter a goal title (or a goal input).", "error");
      return;
    }

    if (metricEnabled && (metricRule.value === "" || metricRule.value == null)) {
      showToast?.("Enter a metric target value.", "error");
      return;
    }

    const isAssigningToOther = canManageOthers && resolvedAssignUserId !== currentUserId;
    const payload = {
      user_id: resolvedAssignUserId,
      title: finalTitle,
      description: description.trim() || null,
      target_value: targetValue !== "" ? Number(targetValue) : null,
      target_unit: resolvedTargetUnit || null,
      due_date: dueDate || null,
      milestones,
      metric_rule: metricEnabled
        ? {
            domain: metricRule.domain,
            field: metricRule.field,
            operator: metricRule.operator,
            value: metricRule.value === "" ? null : Number(metricRule.value),
          }
        : null,
      ...(currentUserIsAdmin && isAssigningToOther ? { assigned_by: true } : {}),
    };

    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/create`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to create goal");
      }

      const out = await res.json();
      onCreated?.(out);
      try {
        window.dispatchEvent(new CustomEvent("goals:refresh"));
      } catch {
        // ignore
      }
      onClose?.();
    } catch (e) {
      showToast?.("Error creating goal: " + (e?.message || String(e)), "error");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 1000005,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "16px",
        overflowY: "auto",
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          width: "min(760px, 100%)",
          background: "var(--bg-modal)",
          border: "1px solid var(--border-light)",
          borderRadius: 16,
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          overflow: "hidden",
          marginTop: 16,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            padding: 16,
            borderBottom: "1px solid var(--border-light)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            position: "sticky",
            top: 0,
            background: "var(--bg-modal)",
            zIndex: 2,
          }}
        >
          <div>
            <div className="heading-4" style={{ marginBottom: 2 }}>
              🎯 Create Goal
            </div>
            <div className="body-small" style={{ color: "var(--text-secondary)" }}>
              Use AI to make it measurable, add milestones, and optionally tie it to a metric.
            </div>
          </div>
          <button type="button" className="btn-secondary" onClick={close} disabled={saving || aiLoading || msLoading}>
            Close
          </button>
        </div>

        <div style={{ padding: 16, overflowY: "auto" }}>
          {canManageOthers && (
            <div className="form-group">
              <label className="body-medium">Assign to User</label>
              <select className="form-input" value={assignUserId} onChange={(e) => setAssignUserId(e.target.value)}>
                <option value="me">Me</option>
                {normalizedPeople.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.name} {u.email ? `(${u.email})` : ""} {u.role ? `— ${u.role}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="body-medium">Goal input (anything)</label>
            <input
              className="form-input"
              value={goalInput}
              onChange={(e) => setGoalInput(e.target.value)}
              placeholder='e.g. "Run a marathon", "Lower body fat by 2%", "Get stronger"'
            />
            <div className="body-small" style={{ color: "var(--text-muted)", marginTop: 6 }}>
              For non-metric goals, milestones are the easiest way to track progress.
            </div>
          </div>

          <div
            style={{
              border: "1px solid var(--border-light)",
              background: "var(--bg-section)",
              borderRadius: 12,
              padding: 12,
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ minWidth: 260 }}>
                <div className="body-medium" style={{ fontWeight: 700 }}>AI Goal Builder</div>
                <div className="body-small" style={{ color: "var(--text-muted)" }}>
                  Fills title, target, unit, and optionally a due date.
                </div>
              </div>

              <button
                type="button"
                className="btn-primary"
                onClick={runAiSuggest}
                disabled={aiLoading}
                style={{ padding: "10px 14px", whiteSpace: "nowrap" }}
              >
                {aiLoading ? "…" : aiSuggestDate ? "✨ Suggest target + due date" : "✨ Suggest target"}
              </button>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12 }}>
              <label className="body-small" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={aiMakeMeasurable} onChange={(e) => setAiMakeMeasurable(e.target.checked)} />
                Make measurable
              </label>

              <label className="body-small" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={aiSuggestDate} onChange={(e) => setAiSuggestDate(e.target.checked)} />
                Suggest due date
              </label>

              <label className="body-small" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={aiUseHistory} onChange={(e) => setAiUseHistory(e.target.checked)} />
                Use history
              </label>

              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="body-small" style={{ color: "var(--text-muted)" }}>Days:</span>
                <input
                  type="number"
                  min="7"
                  max="3650"
                  value={aiHistoryDays}
                  onChange={(e) => setAiHistoryDays((e.target.value))}
                  className="form-input"
                  style={{ width: 110, margin: 0 }}
                  disabled={!aiUseHistory}
                />
              </div>
            </div>
          </div>

          <form onSubmit={submit}>
            <div className="form-group">
              <label className="body-medium">Goal Title</label>
              <input
                className="form-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder='e.g., "Run a marathon"'
              />
            </div>

            <div className="form-group">
              <label className="body-medium">Description</label>
              <textarea
                className="form-input"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Details, constraints, motivation..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="body-medium">Target Value (optional)</label>
                <input
                  type="number"
                  step="0.1"
                  className="form-input"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="body-medium">Unit (optional)</label>
                <select className="form-input" value={targetUnit} onChange={(e) => setTargetUnit(e.target.value)}>
                  <option value="">No unit</option>
                  <option value="miles">miles</option>
                  <option value="km">kilometers</option>
                  <option value="percent">percent</option>
                  <option value="lbs">pounds (lbs)</option>
                  <option value="minutes">minutes</option>
                  <option value="days">days</option>
                  <option value="workouts">workouts</option>
                  <option value="sessions">sessions</option>
                  <option value="steps">steps</option>
                  <option value="calories">calories</option>
                  <option value="reps">reps</option>
                  <option value="custom">Custom…</option>
                </select>

                {targetUnit === "custom" && (
                  <input
                    type="text"
                    className="form-input"
                    style={{ marginTop: 8 }}
                    value={customUnit}
                    onChange={(e) => setCustomUnit(e.target.value)}
                    placeholder="e.g. % body fat, PRs, chapters"
                  />
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="body-medium">Due Date (optional)</label>
              <input type="date" className="form-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>

            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border-light)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>Metric tracking (optional)</div>
                  <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                    Use this when the goal should auto-check against your latest logged metric.
                  </div>
                </div>

                <label className="body-small" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="checkbox" checked={metricEnabled} onChange={(e) => setMetricEnabled(e.target.checked)} />
                  Enable metric rule
                </label>
              </div>

              {metricEnabled && (
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr", marginTop: 12 }}>
                  <div>
                    <label className="body-small" style={{ color: "var(--text-secondary)" }}>Domain</label>
                    <select
                      className="form-input"
                      value={metricRule.domain}
                      onChange={(e) => setMetricRule((prev) => ({ ...prev, domain: e.target.value }))}
                    >
                      {metricDomains.map((domain) => (
                        <option key={domain} value={domain}>
                          {domain}
                          {metricOptions.connected?.[domain] === false ? " (not connected)" : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="body-small" style={{ color: "var(--text-secondary)" }}>Field</label>
                    <select
                      className="form-input"
                      value={metricRule.field}
                      onChange={(e) => setMetricRule((prev) => ({ ...prev, field: e.target.value }))}
                    >
                      {metricFields.map((field) => (
                        <option key={field} value={field}>
                          {field}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="body-small" style={{ color: "var(--text-secondary)" }}>Operator</label>
                    <select
                      className="form-input"
                      value={metricRule.operator}
                      onChange={(e) => setMetricRule((prev) => ({ ...prev, operator: e.target.value }))}
                    >
                      <option value="lt">Less than</option>
                      <option value="gt">Greater than</option>
                      <option value="eq">Equals</option>
                    </select>
                  </div>

                  <div>
                    <label className="body-small" style={{ color: "var(--text-secondary)" }}>Metric target value</label>
                    <input
                      className="form-input"
                      type="number"
                      step="0.1"
                      value={metricRule.value}
                      onChange={(e) => setMetricRule((prev) => ({ ...prev, value: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border-light)" }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>Milestones</div>
              <div className="body-small" style={{ color: "var(--text-secondary)", marginBottom: 10 }}>
                Add milestones manually or generate them with AI. These are saved with the goal when you create it.
              </div>

              <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr", marginBottom: 10 }}>
                <input
                  className="form-input"
                  value={msTitle}
                  onChange={(e) => setMsTitle(e.target.value)}
                  placeholder="Milestone title"
                />
                <input
                  className="form-input"
                  value={msDesc}
                  onChange={(e) => setMsDesc(e.target.value)}
                  placeholder="Description (optional)"
                />
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
                  <input
                    className="form-input"
                    type="number"
                    value={msTargetValue}
                    onChange={(e) => setMsTargetValue(e.target.value)}
                    placeholder="Target value (optional)"
                  />
                  <input
                    className="form-input"
                    value={msTargetUnit}
                    onChange={(e) => setMsTargetUnit(e.target.value)}
                    placeholder="Unit (optional)"
                  />
                </div>
                <input
                  className="form-input"
                  type="date"
                  value={msDueDate}
                  onChange={(e) => setMsDueDate(e.target.value)}
                />
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button type="button" className="btn-secondary" onClick={addMilestoneLocal} disabled={!msTitle.trim()}>
                    + Add milestone
                  </button>

                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <input
                      className="form-input"
                      style={{ width: 90 }}
                      type="number"
                      min={3}
                      max={10}
                      value={msCount}
                      onChange={(e) => setMsCount(Number(e.target.value || 5))}
                    />
                    <button type="button" className="btn-secondary" onClick={generateMilestonesAI} disabled={msLoading}>
                      {msLoading ? "Generating…" : "AI milestones"}
                    </button>
                  </div>
                </div>
              </div>

              {milestones.length > 0 ? (
                <div style={{ display: "grid", gap: 8 }}>
                  {milestones.map((ms, idx) => (
                    <div
                      key={`${ms.title || "milestone"}-${idx}`}
                      style={{
                        border: "1px solid var(--border-light)",
                        borderRadius: 10,
                        padding: 10,
                        background: "var(--bg-secondary)",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 800 }}>{ms.title}</div>
                        <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                          {ms.description ? ms.description : ""}
                          {ms.target_value != null
                            ? ` • Target: ${ms.target_value}${ms.target_unit ? ` ${ms.target_unit}` : ""}`
                            : ""}
                          {ms.due_date ? ` • Due: ${ms.due_date}` : ""}
                        </div>
                      </div>
                      <button type="button" className="btn-secondary" onClick={() => removeMilestoneLocal(idx)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                  No milestones yet.
                </div>
              )}
            </div>

            <button type="submit" className="btn-primary" disabled={saving} style={{ width: "100%", marginTop: 18 }}>
              {saving ? "…" : "Create Goal"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
